import { LaneHistoryResponse } from 'lib/api/getLaneHistory';
import { LaneHistoryFromServiceResponse } from 'lib/api/getLaneHistoryFromService';
import { Maybe } from 'types/UtilityTypes';

export enum AgentInvocationStatus {
  InProgress = 'inProgress',
  Completed = 'completed',
  Failed = 'failed',
}

export enum AgentUserResponseStatus {
  Pending = 'pending',
  AcceptedNoEdits = 'acceptedNoEdits',
  AcceptedWithEdits = 'acceptedWithEdits',
  Rejected = 'rejected',
  NotApplicable = 'notApplicable',
}

export type TaskMode = 'single' | 'batch';

export type StopSummary = {
  stopType: string;
  stopNumber: number;
  city?: string;
  state?: string;
  zip?: string;
  country?: string;
};

export type BuyRateRule = {
  ruleName: string;
  ruleType: string;
  rateAfter: number;
  rateBefore: number;
  adjustmentType: 'percentage' | 'flatAmount' | 'rangePercentile';
  conditionValue: number;
  adjustmentValue: number;
};

export type BuyRateLookupLaneInvocation = {
  quoteRequestId: number;
  stops?: StopSummary[];
  selectedQuoteSource?: string;
  transportType?: string;
  buyRate?: number;
  distance?: number;
  numQuotesReceived?: number;
  numQuoteErrors?: number;
  originalBuyRate?: number;
  /** Low end of provider quote band; omitted when zero (omitempty). */
  buyRateRangeLow?: number;
  /** High end of provider quote band; omitted when zero (omitempty). */
  buyRateRangeHigh?: number;
  appliedRules?: BuyRateRule[];
  distanceSource?: string;
  error?: string;
};

export enum SellRateFlatAmountUnit {
  /** Flat amount applies once to the full trip. */
  TripTotal = 'tripTotal',
  /** Flat amount applies per mile and is multiplied by distance. */
  PerMile = 'perMile',
}

export enum SellRateFuelMode {
  /** Use the default sell-rate fuel behavior for the agent. */
  Default = 'default',
  /** Use the provider fuel surcharge / FSC behavior. */
  Provider = 'provider',
  /** Suppress fuel surcharge handling. */
  None = 'none',
}

/** Percent-based sell rate: margin vs markup (matches backend `PricingFormula`). */
export enum PricingFormula {
  Margin = 'margin',
  Markup = 'markup',
}

export enum SellRatePricingMode {
  AddToBuy = 'addToBuy',
  FixedFinal = 'fixedFinal',
}

/**
 * Snapshot of the deterministic sell-rate rule that drove pricing for this quote.
 * Mirrors `agentmodels.AppliedSellRateRule` in the backend; absent when no rule matched
 * and the agent fell back to its default pricing override.
 */
export type AppliedSellRateRule = {
  ruleName?: string;
  rulePriority: number;
  transportType?: string;
  distanceMiles?: number;
  marginType?: string;
  marginValue?: number;
  pricingFormula?: PricingFormula;
  pricingMode?: SellRatePricingMode;
  /** Flat amount unit returned by the backend rule snapshot. */
  flatAmountUnit?: SellRateFlatAmountUnit;
  /** Fuel handling mode returned by the backend rule snapshot. */
  fuelMode?: SellRateFuelMode;
  fuelAmount?: number;
  rateBeforeRule?: number;
  rateAfterPricing?: number;
  rateAfterFuel?: number;
};

export type SellRateCalculationLaneInvocation = {
  quoteRequestId: number;
  stops?: StopSummary[];
  transportType?: string;
  buyRate?: number;
  sellRate?: number;
  sellRateRounded?: number;
  sellRateRoundingRules?: SellRateRoundingRules;
  marginType?: string;
  marginValue?: number;
  pricingFormula?: PricingFormula;
  pricingMode?: SellRatePricingMode;
  appliedSellRateRule?: AppliedSellRateRule;
  error?: string;
};

export type TaskInvocationMetadata = {
  buyRateLookupTaskInvocation?: {
    mode?: TaskMode;
    quoteRequestId?: number;
    stops?: StopSummary[];
    selectedQuoteSource?: string;
    transportType?: string;
    buyRate?: number;
    distance?: number;
    distanceSource?: string;
    numQuotesReceived?: number;
    numQuoteErrors?: number;
    originalBuyRate?: number;
    /** Low end of provider quote band; omitted when zero */
    buyRateRangeLow?: number;
    /** High end of provider quote band; omitted when zero */
    buyRateRangeHigh?: number;
    appliedRules?: BuyRateRule[];

    laneCount?: number;
    numLanesSucceeded?: number;
    numLanesFailed?: number;
    lanes?: BuyRateLookupLaneInvocation[];
  };
  sellRateCalculationTaskInvocation?: {
    mode?: TaskMode;
    quoteRequestId?: number;
    stops?: StopSummary[];
    buyRate?: number;
    sellRate?: number;
    sellRateRounded?: number;
    sellRateRoundingRules?: SellRateRoundingRules;
    marginType?: string;
    marginValue?: number;
    pricingFormula?: PricingFormula;
    pricingMode?: SellRatePricingMode;
    appliedSellRateRule?: AppliedSellRateRule;

    laneCount?: number;
    numLanesSucceeded?: number;
    numLanesFailed?: number;
    failedLaneQuoteRequestIds?: number[];
    lanes?: SellRateCalculationLaneInvocation[];
  };
  draftEmailTaskInvocation?: {
    generatedEmailId?: number;
  };
};

export type QQAgentTaskInvocation = {
  id: string;
  quickQuoteAgentInvocationId: number;
  quickQuoteAgentId: number;
  agentTaskId: number;
  step: number;
  action: string;
  startedAt: string;
  completedAt: string;
  durationSeconds: number;
  status: AgentInvocationStatus;
  retries: number;
  error?: string;
  braintrustSpanId: string;
  metadata: TaskInvocationMetadata;
};

export type QQAgentInvocation = {
  id: number;
  startedAt: string;
  completedAt: string;
  durationSeconds: number;
  status: AgentInvocationStatus;
  userResponseStatus: AgentUserResponseStatus;
  error?: string;
  quickQuoteAgentId: number;
  agentDisabled: boolean;
  batchQuoteId?: number;
  taskInvocations: QQAgentTaskInvocation[];
  // Written-rules runs (engine "llm"; the backend calls this V2) carry reasoning.
  engine?: QQAgentEngine;
  ruleSetId?: number;
  reasoning?: Maybe<AgentReasoning>;
};

export enum QQAgentEngine {
  Structured = 'structured',
  LLM = 'llm',
}

export enum LaneOutcome {
  Quote = 'quote',
  NoBid = 'noBid',
  NeedsReview = 'needsReview',
  Error = 'error',
}

export enum LedgerOp {
  Base = 'base',
  MarginPct = 'marginPct',
  MarkupPct = 'markupPct',
  AddFlat = 'addFlat',
  AddPct = 'addPct',
  MinTotal = 'minTotal',
  MaxTotal = 'maxTotal',
  SetTotal = 'setTotal',
  Round = 'round',
  Guardrail = 'guardrail',
}

export enum RuleEvaluationStatus {
  Applied = 'applied',
  NotApplicable = 'notApplicable',
  CouldNotEvaluate = 'couldNotEvaluate',
}

export enum ReasoningConfidence {
  High = 'high',
  Moderate = 'moderate',
  Low = 'low',
}

export type LedgerStep = {
  ruleId?: string;
  op: LedgerOp;
  value: number;
  before: number;
  after: number;
  label: string;
};

export type RuleEvaluation = {
  ruleId: string;
  ruleText: string;
  status: RuleEvaluationStatus;
  evidence: string;
};

export type ConflictResolution = {
  ruleIds: string[];
  resolution: string;
};

export type ReasoningSource = {
  source: string;
  label: string;
  rate: number;
  low?: number;
  high?: number;
  confidence?: number;
  isDemo?: boolean;
};

export enum DataSourceKind {
  DAT = 'dat',
  Greenscreens = 'greenscreens',
  TMSLaneHistory = 'tmsLaneHistory',
}

export type RecentLaneLoad = {
  loadId: string;
  pickupDate: string;
  carrierCost: number;
  customerRate: number;
  marginPct: number;
};

// A rate source Kit looked at for a lane. laneHistory has the same shape as the sidebar's
// lane-history responses, so the existing charts can render it.
export type ReasoningDataSource = {
  kind: DataSourceKind | string;
  label: string;
  summary?: string;
  rate?: number;
  low?: number;
  high?: number;
  confidence?: number;
  usedAsBase?: boolean;
  isDemo?: boolean;
  laneHistory?: Maybe<LaneHistoryResponse | LaneHistoryFromServiceResponse>;
  recentLoads?: Maybe<RecentLaneLoad[]>;
};

export type JudgeIssue = {
  ruleId: string;
  problem: string;
};

export type JudgeVerdict = {
  pass: number;
  passed: boolean;
  issues: Maybe<JudgeIssue[]>;
};

// Go serializes nil slices as null, so every list here may be null.
export type LaneReasoning = {
  quoteRequestId?: number;
  laneLabel: string;
  outcome: LaneOutcome;
  buyRate?: number;
  sellRate?: number;
  marginPct?: number;
  miles?: number;
  baseSource?: string;
  steps: Maybe<LedgerStep[]>;
  ruleEvaluations: Maybe<RuleEvaluation[]>;
  conflicts: Maybe<ConflictResolution[]>;
  flags: Maybe<string[]>;
  shortReasoning: string;
  // Empty when the lane couldn't be priced.
  confidence: ReasoningConfidence | '';
  sources: Maybe<ReasoningSource[]>;
  // Richer per-source detail; older payloads only have sources.
  dataSources?: Maybe<ReasoningDataSource[]>;
  judge: Maybe<JudgeVerdict[]>;
  noBidRuleId?: string;
  errorMessage?: string;
};

export type AgentReasoning = {
  lanes: Maybe<LaneReasoning[]>;
  model: string;
  judgeModel?: string;
  judgePasses: number;
  totalTokens: number;
  costUsd: number;
  durationMs: number;
  dryRun?: boolean;
};

// Rule set summary per invocation, served alongside invocations for written-rules runs.
export type InvocationRuleSetSummary = {
  id: number;
  version: number;
  agentName: string;
  supportedCount: number;
  totalCount: number;
};

export enum QQAgentFeedbackRating {
  Up = 'up',
  Down = 'down',
}

export type SubmitQQAgentFeedbackParams = {
  invocationId: number;
  rating: QQAgentFeedbackRating;
  note?: string;
};

export type DATLocationMarketInfo = {
  stopLocation: string;
  inboundLoadToTruckRatio: string;
  inboundMarketConditionsIndexPrevDay?: number;
  inboundMarketConditionsIndexPrevWeek?: number;
  inboundMarketConditionsForecastNextDay?: number;
  outboundLoadToTruckRatio: string;
  outboundMarketConditionsIndexPrevDay?: number;
  outboundMarketConditionsIndexPrevWeek?: number;
  outboundMarketConditionsForecastNextDay?: number;
};

export type DATMarketInfo = {
  pickupMarketInformation: DATLocationMarketInfo;
  dropoffMarketInformation: DATLocationMarketInfo;
};

// Slim quick quote data returned alongside QQ Agent Invocations
export type QuickQuoteSlim = {
  id: number;
  quickQuoteAgentInvocationId?: number;
  source: string;
  typeInSource: string;
  datMarketInfo?: DATMarketInfo;
};

export type GetQQAgentInvocationsParams = {
  threadId: string;
};

export type GetQQAgentInvocationsResponse = {
  invocations: QQAgentInvocation[];
  quickQuotesByInvocationId: Record<string, QuickQuoteSlim[]>;
  ruleSetsByInvocationId?: Record<string, InvocationRuleSetSummary>;
};

export type SellRateRoundingRules = {
  // RoundToNearest specifies the dollar amount to round to (e.g., 1, 5, 10, 25)
  // If null, no rounding is applied
  roundToNearest?: number;
  // RoundUp determines rounding direction: true rounds up, false rounds down
  roundUp: boolean;
};

export type BuyRateData = NonNullable<
  NonNullable<TaskInvocationMetadata['buyRateLookupTaskInvocation']>
>;

export type SellRateData = NonNullable<
  NonNullable<TaskInvocationMetadata['sellRateCalculationTaskInvocation']>
>;
